import { AppIcon } from '@/components/ui/app-icon';
import { useState } from 'react';
import { useOrganization, useUser } from '@clerk/react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GoogleDocsIcon } from '@/components/ui/icons/google-product-icons';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import {
  googlePickError,
  useGooglePicker,
} from '@/hooks/use-google-picker.hook';
import { iconArrowUpRightFromSquare, iconXmark } from '@/lib/icons';
import type {
  GoogleDocRef,
  GoogleDocTriggerConfig,
  TriggerConfig,
} from '@/types/custom-playbooks';
import { useAgentPlay } from '../agent-play-context';
import { ConfigField, ConfigSelect } from './primitives';
import {
  MAX_GOOGLE_DOCS,
  docIdOf,
  googleDocUrl,
  withDocs,
} from './google-doc.util';

/** The Connect Google Docs state both Google Docs panels show. */
export function ConnectGoogleDocs({ reconnect }: { reconnect?: boolean }) {
  return (
    <div
      className='flex items-center gap-2.5 rounded-lg border border-border/60 bg-background p-3'
      data-testid='connect-google-docs'
    >
      <GoogleDocsIcon className='h-[18px] w-[18px]' />
      <p className='min-w-0 flex-1 text-[12px] font-medium text-foreground'>
        {reconnect ? 'Reconnect Google Docs' : 'Connect Google Docs'}
      </p>
      <Link
        to='/integrations'
        className='flex items-center gap-1 text-[11px] text-tertiary-foreground hover:text-foreground'
      >
        Integrations
        <AppIcon icon={iconArrowUpRightFromSquare} style={{ fontSize: 9 }} />
      </Link>
    </div>
  );
}

/**
 * The trigger "A picked Google Doc is updated": up to 20 docs, picked with
 * Google when Google Docs is connected or pasted as links when it isn't. Any
 * change to the docs names the editing member, whose Google reads them.
 * Hidden unless it is an agent's play with Agents unlocked (hidden while it
 * loads).
 */
export function GoogleDocTriggerConfig({
  config,
  onChange,
  readOnly,
}: {
  config: Partial<TriggerConfig>;
  onChange: (next: TriggerConfig) => void;
  readOnly?: boolean;
}) {
  const agentPlay = useAgentPlay();
  const { unlocked } = useAgentsAccess();
  const { organization, isLoaded } = useOrganization();
  const { user } = useUser();
  const picker = useGooglePicker('google_docs');
  const [link, setLink] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  if (!agentPlay || !unlocked || !isLoaded || !organization) return null;

  const block: GoogleDocTriggerConfig = {
    documents: [],
    backfill: 'now',
    ...config.googleDoc,
  };
  const docs = block.documents ?? [];
  const write = (patch: Partial<GoogleDocTriggerConfig>, rebind: boolean) => {
    if (readOnly) return;
    const googleDoc: GoogleDocTriggerConfig = { ...block, ...patch };
    if (rebind && user?.id) {
      googleDoc.googleUserId = user.id;
      if (picker.accountLabel) googleDoc.accountLabel = picker.accountLabel;
      else delete googleDoc.accountLabel;
    }
    const next: TriggerConfig = {
      ...(config as TriggerConfig),
      source: 'google_doc',
      kind: 'google_doc_updated',
      googleDoc,
    };
    delete next.rerunAfterDays;
    delete next.skipTouchedWithinDays;
    onChange(next);
  };
  const setDocs = (documents: GoogleDocRef[]) => write({ documents }, true);

  const addLink = () => {
    const docId = docIdOf(link);
    if (!docId) {
      setError('Paste a Google Docs link');
      return;
    }
    if (docs.some((doc) => docIdOf(doc.docId) === docId)) {
      setError('Pick each doc once');
      return;
    }
    if (docs.length >= MAX_GOOGLE_DOCS) {
      setError('Pick or paste 1 to 20 Google Docs');
      return;
    }
    setError(null);
    setLink('');
    setDocs(withDocs(docs, [{ docId, url: googleDocUrl(docId) }]));
  };

  const pick = async () => {
    setPicking(true);
    setError(null);
    try {
      // Pasted docs are offered first, so one pick grants access to them;
      // every other doc is in the same pick too.
      const pasted = docs
        .filter((doc) => !doc.title)
        .map((doc) => docIdOf(doc.docId))
        .filter((id): id is string => !!id);
      const picked = await picker.pickDocs(pasted.length ? pasted : undefined);
      if (!picked.length) return;
      const named = new Map(picked.map((file) => [file.id, file]));
      const kept = docs.map((doc) => {
        const file = named.get(docIdOf(doc.docId) ?? '');
        return file
          ? { docId: file.id, title: file.name, url: file.url || doc.url }
          : doc;
      });
      setDocs(
        withDocs(
          kept,
          picked.map((file) => ({
            docId: file.id,
            title: file.name,
            url: file.url || googleDocUrl(file.id),
          }))
        )
      );
    } catch (err) {
      setError(googlePickError(err));
    } finally {
      setPicking(false);
    }
  };

  const account = block.accountLabel || picker.accountLabel;
  return (
    <div className='space-y-4' data-testid='google-doc-trigger-config'>
      <ConfigField
        label='Google Docs'
        action={
          picker.connected ? (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-7 px-2.5 text-xs'
              disabled={readOnly || picking || docs.length >= MAX_GOOGLE_DOCS}
              onClick={() => void pick()}
            >
              {picking ? 'Opening Google…' : 'Pick docs'}
            </Button>
          ) : undefined
        }
      >
        {!picker.connected && <ConnectGoogleDocs />}
        {docs.length > 0 && (
          <ul className='space-y-1.5'>
            {docs.map((doc, index) => {
              const id = docIdOf(doc.docId);
              return (
                <li
                  key={`${doc.docId}-${index}`}
                  className='flex items-center gap-2 rounded-lg border border-border/60 bg-background px-2.5 py-2'
                >
                  <GoogleDocsIcon className='h-4 w-4 shrink-0' />
                  <a
                    href={doc.url || (id ? googleDocUrl(id) : undefined)}
                    target='_blank'
                    rel='noreferrer'
                    className='min-w-0 flex-1 truncate text-[12px] font-medium text-foreground hover:underline'
                  >
                    {doc.title || id || doc.docId}
                  </a>
                  <button
                    type='button'
                    aria-label={`Remove ${doc.title || id || 'doc'}`}
                    disabled={readOnly}
                    className='shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50'
                    onClick={() =>
                      setDocs(docs.filter((_, at) => at !== index))
                    }
                  >
                    <AppIcon icon={iconXmark} style={{ fontSize: 11 }} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className='flex items-center gap-2'>
          <Input
            aria-label='Google Docs link'
            value={link}
            disabled={readOnly || docs.length >= MAX_GOOGLE_DOCS}
            placeholder='https://docs.google.com/document/d/…'
            className='h-9 text-sm'
            onChange={(event) => {
              setLink(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addLink();
              }
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-9 shrink-0 px-3 text-xs'
            disabled={readOnly || !link.trim()}
            onClick={addLink}
          >
            Add
          </Button>
        </div>
        {account && (
          <p className='text-[11px] text-tertiary-foreground'>{account}</p>
        )}
        {error && (
          <p role='alert' className='text-xs text-destructive'>
            {error}
          </p>
        )}
      </ConfigField>
      <ConfigField label='Start with'>
        <ConfigSelect
          ariaLabel='Google Docs start from'
          value={block.backfill ?? 'now'}
          disabled={readOnly}
          options={[
            { value: 'now', label: 'Edits after you turn on the play' },
            { value: 'historic', label: 'The docs as they are now' },
          ]}
          onChange={(backfill) =>
            write({ backfill: backfill as 'now' | 'historic' }, false)
          }
        />
      </ConfigField>
    </div>
  );
}
